import React, { type ReactNode } from 'react';

import { Box, Divider, Drawer, Toolbar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import AstroLogoElement from '@client/components/general/AstroLogoElement';
import UserAvatarMenu from '@client/components/userAvatar/UserAvatarMenu';
import { navigationPaths } from '@constants/navigation';
import { sizes } from '@constants/sizes';
import { colors } from '@styles/theme';

import SidebarMenuList from './SidebarMenuList';

const Sidebar = ({
  drawerWidth,
  handleDrawerToggle,
  mobileOpen,
}: {
  drawerWidth: number;
  handleDrawerToggle: () => void;
  mobileOpen: boolean;
}) => {
  const theme = useTheme();

  const isLargeScreen = useMediaQuery('(min-width: 900px)');
  // decides how drawer will behave - permanent for desktop || temporary for mobile
  const ResponsiveDrawer = ({ children }: { children: ReactNode }) => {
    if (useMediaQuery(theme.breakpoints.up('md'))) {
      return (
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant='permanent'
          anchor='left'>
          {children}
        </Drawer>
      );
    } else {
      return (
        <Drawer
          variant='temporary'
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}>
          {children}
        </Drawer>
      );
    }
  };

  return (
    <ResponsiveDrawer>
      <Toolbar sx={{ width: '100%', height: sizes.navBarHeight }} disableGutters>
        <Box sx={{ pl: 2 }}>
          <AstroLogoElement logoVersion='full' href={navigationPaths.home.app} />
        </Box>
      </Toolbar>
      <Divider sx={{ borderColor: colors.component.paperBorderColor }} />
      <SidebarMenuList />

      {isLargeScreen && <UserAvatarMenu />}
    </ResponsiveDrawer>
  );
};

export default Sidebar;
